import { parseAsArrayOf, parseAsString, parseAsInteger, useQueryStates } from 'nuqs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { modelService } from '@/modules/gpt-chats/services/aimodel.service';
import {
  ICreateModelPayload,
  IModelListPayload,
  IUpdateModelPayload,
} from '@/modules/gpt-chats/types/aimodel.service.type';

export const useAIModelsQueryParams = () => {
  const [queryParams, setQueryParams] = useQueryStates({
    search: parseAsString.withDefault(''),
    types: parseAsArrayOf(parseAsString).withDefault([]),
    page: parseAsInteger.withDefault(1),
    page_size: parseAsInteger.withDefault(10),
  });

  return { queryParams, setQueryParams };
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['model', 'create'],
    mutationFn: (payload: ICreateModelPayload) => modelService.createModel(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      queryClient.invalidateQueries({ queryKey: ['model'] });
    },
  });
};
export const useCreateAndValidateModel = () => {
  const queryClient = useQueryClient();
  const validateMutation = useValidateModel();

  return useMutation({
    mutationKey: ['model', 'create-and-validate'],
    // variables = { payload, project_key }
    mutationFn: ({ payload }: { payload: ICreateModelPayload }) =>
      modelService.createModel(payload),
    onSuccess: async (res) => {
      // variables is the same object we passed into mutate()
      queryClient.invalidateQueries({ queryKey: ['models'] });

      if (res?.item_id) {
        validateMutation.mutate({
          modelId: res.item_id,
        });
      }
    },
  });
};

export const useGetModels = (payload: IModelListPayload) => {
  return useQuery({
    queryKey: ['models', payload],
    queryFn: () => modelService.getModels(payload),
  });
};

export const useGetAllModels = (payload: IModelListPayload) => {
  return useQuery({
    queryKey: ['models', payload],
    queryFn: () => modelService.getAllModels(payload),
  });
};

export const useGetModelById = (modelId: string) => {
  return useQuery({
    queryKey: ['model', modelId],
    queryFn: () => modelService.getModelById(modelId),
    enabled: !!modelId,
  });
};

export const useUpdateModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['model', 'update'],
    mutationFn: ({ modelId, payload }: { modelId: string; payload: IUpdateModelPayload }) =>
      modelService.updateModel(modelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model'] });
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });
};

export const useDeleteModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['model', 'delete'],
    mutationFn: ({ modelId }: { modelId: string }) => modelService.deleteModel(modelId),
    onSuccess: (res) => {
      if (res?.is_success) {
        queryClient.invalidateQueries({ queryKey: ['models'] });
        queryClient.invalidateQueries({ queryKey: ['model'] });
      }
    },
  });
};

export const useValidateModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['model', 'validate'],
    mutationFn: ({ modelId }: { modelId: string }) => modelService.validateModel(modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model'] });
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });
};

export const useSeedProviders = () => {
  return useQuery({
    queryKey: ['seed-providers'],
    queryFn: () => modelService.getSeedProviders(),
  });
};

export const useSeedModelsByProvider = (provider: string) => {
  return useQuery({
    queryKey: ['seed-models', provider],
    queryFn: () => modelService.getSeedModelsByProvider(provider),
    enabled: !!provider,
  });
};
